import * as React from 'react';
import { importEndpoints } from '@ooh/contracts';
import { useApiClient } from '../../plugs/api-client';
import { TableSkeleton } from '../../layouts/skeletons';
import { resolvePhaseFromJobStatus } from '../lib/mapping-phase-wizard-helpers';

type OnPhaseChange = (phase: string, jobId?: number) => void;

interface MappingConflictRedirectProps {
  importJobId: number;
  onPhaseChange: OnPhaseChange;
}

/** Sends the wizard to the phase the job is actually in, for a stale mapping step. */
export function useRedirectToCurrentPhase(importJobId: number, onPhaseChange: OnPhaseChange) {
  const api = useApiClient();
  return React.useCallback(async () => {
    const status = await api.call(importEndpoints.getStatus, { id: importJobId }).then(
      (job) => job.status,
      () => undefined,
    );
    onPhaseChange(resolvePhaseFromJobStatus(status), importJobId);
  }, [api, importJobId, onPhaseChange]);
}

/**
 * A 409 from the columns endpoint means the job already left the mapping step (stale
 * ?phase=mapping URL revisited after confirm, double-submit); it would 409 forever on every
 * retry, so jump to the job's actual phase instead of offering one.
 */
export function MappingConflictRedirect({
  importJobId,
  onPhaseChange,
}: MappingConflictRedirectProps) {
  const redirect = useRedirectToCurrentPhase(importJobId, onPhaseChange);
  React.useEffect(() => {
    void redirect();
  }, [redirect]);
  return <TableSkeleton />;
}
